import { useState, useEffect, Suspense } from 'react'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import Sidebar from './Sidebar'
import ScrollToTop from './ScrollToTop'
import RouteFallback from './RouteFallback'
import RouteErrorBoundary from './RouteErrorBoundary'
import { CATEGORIES } from '../data/tools'
import './Layout.css'

function Layout() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light'
    const saved = localStorage.getItem('webtoolz-theme')
    if (saved === 'light' || saved === 'dark') return saved
    return 'light'
  })
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.innerWidth > 1024
  })
  const location = useLocation()
  const navigate = useNavigate()
  const isHomePage = location.pathname === '/'

  useEffect(() => {
    const redirectPath = sessionStorage.getItem('redirectPath')
    if (redirectPath && redirectPath !== '/') {
      sessionStorage.removeItem('redirectPath')
      navigate(redirectPath, { replace: true })
    }
  }, [])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('webtoolz-theme', theme)
  }, [theme])

  return (
    <div className={`app-layout ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
      <ScrollToTop />
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="main-wrapper">
        <header className="top-header">
          <button
            type="button"
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger"></span>
            <span className="hamburger"></span>
            <span className="hamburger"></span>
          </button>

          <Link to="/" className="header-brand" aria-label="WebToolz365 — Home">
            <img
              className="header-brand-logo"
              src="/logo.svg"
              alt=""
              width="288"
              height="56"
              decoding="async"
            />
            <span className="visually-hidden">WebToolz365</span>
          </Link>

          <div className="header-actions">
            <button
              type="button"
              className="theme-toggle"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span aria-hidden>{theme === 'dark' ? '☀️' : '🌙'}</span>
              <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
            </button>
            <Link to="/about" className="header-link" title="About">
              About
            </Link>
            <Link to="/about#linking" className="header-link header-link-muted" title="Badges and embed code">
              Link to us
            </Link>
            <a
              href="https://github.com/Faizgeeky"
              target="_blank"
              rel="noopener noreferrer"
              className="github-link"
              title="WebToolz365 on GitHub"
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
            </a>
          </div>
        </header>

        <main className="main-content" id="main" tabIndex={-1}>
          <RouteErrorBoundary key={location.pathname}>
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </main>

        <footer className="main-footer">
          <div className="footer-container">
            <div className="footer-grid">
              <div className="footer-brand">
                <span className="footer-brand-name">WebToolz365</span>
                <p className="footer-tagline">Free developer &amp; utility tools — client-side first.</p>
              </div>
              <nav className="footer-nav" aria-label="Footer">
                {CATEGORIES.map((c) => (
                  <Link key={c.id} to={`/${c.route}`}>
                    {c.navTitle}
                  </Link>
                ))}
                <Link to="/blog">Blog</Link>
                <Link to="/about">About</Link>
                <Link to="/contact">Contact</Link>
                <Link to="/privacy-policy">Privacy</Link>
                <Link to="/terms-of-service">Terms</Link>
                <a href="https://github.com/Faizgeeky/WebToolsz" target="_blank" rel="noopener noreferrer">
                  GitHub
                </a>
              </nav>
            </div>
            <div className="footer-bottom">
              <p>
                &copy; {new Date().getFullYear()} WebToolz365
                {isHomePage ? ' — tools run in your browser where possible.' : '.'}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default Layout
